import React, { useEffect, useRef, useState, useMemo } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import {
  Activity,
  BookOpen,
  BookUp,
  ThumbsUp,
  Clipboard,
  ClipboardList,
  MessageSquare,
  Unlink,
  Mail,
  Users,
  LineChart,
  AlertCircle,
  Search,
  Network,
  ServerCrash,
  AlignStartVertical,
  UsersRound,
  BarChart3,
  Archive,
  Menu,
  Mails,
  MailCheck,
  X,
  DatabaseBackup,
  FileQuestion,
  ListChecks,
  Video,
  Shield,
  ScrollText,
  Bell,
  User,
  LogOut
} from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import './AdminSidebar.v2.css'

// `activePaths` lists additional URL prefixes that should keep an item
// highlighted as active, beyond its own `path`. We use this for create/edit
// routes (e.g. /abstimmung/bearbeiten_v2/:id should activate "Abstimmungen").
const menuCategories = [
  {
    title: 'Allgemein',
    items: [
      { icon: Activity, text: 'Nutzeraktivitäten', path: '/activity', permissionKey: 'activity' }
    ]
  },
  {
    title: 'Leitlinien',
    items: [
      { icon: BookOpen, text: 'Gespeicherte Leitlinien', path: '/leitlinien', permissionKey: 'leitlinien' },
      { icon: BookOpen, text: 'Stage Leitlinien', path: '/stageleitlinien', permissionKey: 'stageleitlinien' },
      { icon: BookOpen, text: 'Live-Arbeitsversionen', path: '/livearbeitsversionen', permissionKey: 'livearbeitsversionen' }
    ]
  },
  {
    title: 'Umfragen',
    items: [
      { icon: BarChart3, text: 'Abstimmungs-Analytics', path: '/abstimmungen-analytics', permissionKey: 'abstimmungen-analytics' },
      { icon: ThumbsUp, text: 'Abstimmungen', path: '/abstimmungen', permissionKey: 'abstimmungen',
        // v2 page. Precise prefixes so legacy edit URLs don't also light this
        // up (note: '/abstimmung/bearbeiten/' does NOT prefix-match
        // '/abstimmung/bearbeiten_v2/', they diverge at the underscore).
        activePaths: ['/abstimmung/bearbeiten_v2/', '/abstimmung/erstellen'] },
      { icon: Archive, text: 'Alte Abstimmungen', path: '/abstimmungen-alt', permissionKey: 'abstimmungen',
        // Legacy page. Sibling path (NOT nested under /abstimmungen, or the
        // startsWith match in isItemActive would highlight both entries).
        activePaths: ['/abstimmung/bearbeiten/'] },
      { icon: MessageSquare, text: 'Feedback', path: '/feedback', permissionKey: 'feedback' },
      { icon: UsersRound, text: 'Konsultationen', path: '/konsultation', permissionKey: 'konsultation' },
      { icon: Clipboard, text: 'Umfrage zum Updatebedarf', path: '/abfragen', permissionKey: 'abfragen',
        activePaths: ['/abfrage/'] },
      { icon: Users, text: 'Autor*innen', path: '/authors', permissionKey: 'authors' },
      { icon: ListChecks, text: 'Fragen-Vorlagen', path: '/fragenvorlagen', permissionKey: 'fragenvorlagen' }
    ]
  },
  {
    title: 'Hub',
    items: [
      { icon: AlignStartVertical, text: 'Hub Analytics', path: '/hub-analytics', permissionKey: 'hub-analytics' },
      { icon: BookUp, text: 'Hub Leitlinien', path: '/hub-leitlinien', permissionKey: 'hub-leitlinien' }
    ]
  },
  {
    title: 'Mobile App',
    items: [
      { icon: LineChart, text: 'App-Analytics', path: '/app-analytics', permissionKey: 'app-analytics' },
      { icon: Bell, text: 'Push-Nachrichten', path: '/app-benachrichtigungen', permissionKey: 'app-benachrichtigungen' },
      { icon: ClipboardList, text: 'Leitlinien-Umfragen', path: '/leitlinien-umfragen', permissionKey: 'leitlinien-umfragen' },
      { icon: MessageSquare, text: 'Leitlinien-Kommentare', path: '/leitlinien-kommentare', permissionKey: 'leitlinien-kommentare' }
    ]
  },
  {
    title: 'Tools',
    items: [
      { icon: Mails, text: 'Newsletter', path: '/newsletter', permissionKey: 'newsletter' },
      { icon: FileQuestion, text: 'E-Mail Vorlagen', path: '/emailvorlagen', permissionKey: 'emailvorlagen' },
      { icon: Search, text: 'Deep Guidelines Search', path: '/deepsearch', permissionKey: 'deepsearch' },
      { icon: Network, text: 'Fachgesellschaften', path: '/fachgesellschaftengraph', permissionKey: 'fachgesellschaftengraph',
        // Old standalone visualisation URLs, now redirects into this page.
        activePaths: ['/fachgesellschaften'] },
      { icon: Unlink, text: 'Defekte URLs', path: '/brokenurls', permissionKey: 'brokenurls' },
      { icon: MailCheck, text: 'E-Mail-Zustellung', path: '/email-zustellung', permissionKey: '_email_zustellung_admin' },
      { icon: AlertCircle, text: 'Website Crashes', path: '/errorsdisplay', permissionKey: '_errorsdisplay_super_admin' },
      { icon: DatabaseBackup, text: 'Backups', path: '/backups', permissionKey: '_backups_super_admin' }
    ]
  },
  {
    title: 'Verwaltung',
    items: [
      { icon: Shield, text: 'Admin-Verwaltung', path: '/admin-verwaltung', permissionKey: '_admin_management' },
      { icon: ScrollText, text: 'Audit Log', path: '/audit-log', permissionKey: '_audit_log' }
    ]
  }
]

// True when the current URL should keep this item highlighted as active.
// Matches by exact path, by prefix (path + '/'), or against any of the
// item's optional `activePaths` prefixes (used for edit/create routes that
// live under a different URL than the listing page).
function isItemActive(item, currentPath) {
  if (!currentPath || !item) return false
  if (currentPath === item.path) return true
  if (item.path !== '/' && currentPath.startsWith(item.path + '/')) return true
  if (Array.isArray(item.activePaths)) {
    return item.activePaths.some(p => currentPath === p || currentPath.startsWith(p))
  }
  return false
}

const SCROLL_KEY = 'adminSidebarScrollTop'

function SidebarContent({ onItemClick, variant }) {
  const scrollRef = useRef(null)
  const activeItemRef = useRef(null)
  const { user, hasPermission, hasRole, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const currentPath = location.pathname

  useEffect(() => {
    const saved = sessionStorage.getItem(SCROLL_KEY)
    if (saved && scrollRef.current) {
      scrollRef.current.scrollTop = parseInt(saved, 10)
    }
  }, [])

  const handleScroll = () => {
    if (scrollRef.current) {
      sessionStorage.setItem(SCROLL_KEY, scrollRef.current.scrollTop)
    }
  }

  // Filter menu items based on permissions
  const visibleCategories = useMemo(() => {
    return menuCategories
      .map(cat => ({
        ...cat,
        items: cat.items.filter(item => {
          // Special permission keys for admin-only sections
          if (item.permissionKey === '_admin_management') {
            return hasRole('admin', 'super_admin')
          }
          if (item.permissionKey === '_audit_log'
              || item.permissionKey === '_email_zustellung_admin') {
            return hasRole('admin', 'super_admin')
          }
          if (item.permissionKey === '_backups_super_admin'
              || item.permissionKey === '_errorsdisplay_super_admin') {
            return hasRole('super_admin')
          }
          return hasPermission(item.permissionKey)
        })
      }))
      .filter(cat => cat.items.length > 0)
  }, [user, hasPermission, hasRole])

  // Keep the current route visible in long sidebars. Preserve the user's
  // saved position when the active item is already on screen; otherwise
  // centre only that item inside the sidebar's own scroll container.
  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      const container = scrollRef.current
      const activeItem = activeItemRef.current
      if (!container || !activeItem) return
      const containerRect = container.getBoundingClientRect()
      const itemRect = activeItem.getBoundingClientRect()
      const itemIsVisible = itemRect.top >= containerRect.top && itemRect.bottom <= containerRect.bottom
      if (!itemIsVisible) {
        container.scrollTop += itemRect.top - containerRect.top - (containerRect.height - itemRect.height) / 2
        sessionStorage.setItem(SCROLL_KEY, container.scrollTop)
      }
    })
    return () => window.cancelAnimationFrame(frame)
  }, [currentPath, visibleCategories])

  const v2 = variant === 'v2'
  return (
    <div
      ref={scrollRef}
      onScroll={handleScroll}
      className={
        (v2 ? 'admin-sidebar--v2 ' : '') +
        'h-full w-full rounded-xl border-2 border-gray-300 bg-[#faf9f6] shadow-[1px_1px_0_#faf9f6] overflow-y-auto pb-6 flex flex-col'
      }
    >
      <div className="flex-1">
        {visibleCategories.map((category, index) => (
          <div key={category.title} className={`pt-4 ${index !== 0 ? 'mt-4 border-t border-gray-300' : ''}`}>
            <h3 className="px-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">
              {category.title}
            </h3>
            <div className="mt-2 space-y-2">
              {category.items.map((item) => {
                const { icon: Icon, text, path, iconStyle } = item
                const active = isItemActive(item, currentPath)
                return (
                  <NavLink
                    key={path}
                    ref={active ? activeItemRef : undefined}
                    to={path}
                    className={
                      `flex items-center gap-3 px-3 py-2 mx-2 cursor-pointer rounded-md transition-colors no-underline hover:no-underline ${
                        active ? 'bg-black shadow-[inset_2px_2px_0_rgba(255,255,255,0.15)] text-white' : 'hover:bg-gray-300'
                      }`
                    }
                    onClick={onItemClick}
                  >
                    <Icon
                      size={20}
                      color={active ? '#ffffff' : '#000000'}
                      className={`w-5 h-5 ${iconStyle || ''}`}
                    />
                    <span className={`text-sm font-medium ${active ? 'text-white' : 'text-gray-800'}`}>
                      {text}
                    </span>
                  </NavLink>
                )
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Bottom section: Profile + Logout */}
      <div className="mt-4 pt-4 border-t border-gray-300 px-2 space-y-1">
        <button
          onClick={() => { navigate('/profile'); onItemClick?.() }}
          className="flex items-center gap-3 px-3 py-2 w-full rounded-md transition-colors hover:bg-gray-300 text-left"
        >
          <User size={18} color="#000" />
          <span className="text-sm font-medium text-gray-800 truncate">
            Mein Profil
          </span>
        </button>
        <button
          onClick={logout}
          className="flex items-center gap-3 px-3 py-2 w-full rounded-md transition-colors hover:bg-red-100 text-left"
        >
          <LogOut size={18} color="#dc2626" />
          <span className="text-sm font-medium text-red-600">Abmelden</span>
        </button>
      </div>
    </div>
  )
}

function AdminSidebar({ side = 'left', variant = 'v2', desktopBreakpoint = 'md' }) {
  const [open, setOpen] = useState(false)
  const location = useLocation()
  const isRight = side === 'right'

  useEffect(() => {
    setOpen(false)
  }, [location.pathname])

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [open])

  const desktopAnchor = isRight ? 'right-0' : 'left-0'
  const mobileBtnAnchor = isRight ? 'right-3' : 'left-3'
  const drawerAnchor = isRight ? 'right-0' : 'left-0'
  const drawerHidden = isRight ? 'translate-x-full' : '-translate-x-full'
  const closeBtnAnchor = isRight ? 'left-[-12px]' : '-right-3'
  const desktopOnly = desktopBreakpoint === 'lg' ? 'hidden lg:block' : 'hidden md:block'
  const drawerOnly = desktopBreakpoint === 'lg' ? 'lg:hidden' : 'md:hidden'

  return (
    <>
      <button
        aria-label="Menü öffnen"
        aria-expanded={open}
        aria-controls="admin-mobile-menu"
        className={`fixed top-3 ${mobileBtnAnchor} ${drawerOnly} grid h-11 w-11 place-items-center rounded-lg border border-gray-300 bg-white shadow`}
        onClick={() => setOpen(true)}
        style={{zIndex: 25}}
      >
        <Menu className="w-5 h-5" />
      </button>

      <nav
        className={
          `fixed top-0 ${desktopAnchor} h-screen w-[250px] p-1 z-10 ${desktopOnly}` +
          (variant === 'v2' ? ' admin-sidebar-nav--v2' : '')
        }
      >
        <SidebarContent variant={variant} />
      </nav>

      <div
        className={`fixed inset-0 z-30 bg-black/40 transition-opacity ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'} ${drawerOnly}`}
        onClick={() => setOpen(false)}
      />

      <nav
        id="admin-mobile-menu"
        className={`fixed top-0 ${drawerAnchor} z-40 h-full w-[270px] p-2 transform transition-transform duration-300 ${drawerOnly} ${
          open ? 'translate-x-0' : drawerHidden
        }`}
        role={open ? 'dialog' : undefined}
        aria-modal={open ? 'true' : undefined}
        aria-hidden={!open}
        inert={!open ? '' : undefined}
        style={{zIndex: 40}}
      >
        <div className="relative h-full">
          <button
            aria-label="Menü schließen"
            className={`absolute ${closeBtnAnchor} top-3 z-50 grid h-11 w-11 place-items-center rounded-full border border-gray-300 bg-white shadow`}
            onClick={() => setOpen(false)}
          >
            <X className="w-5 h-5" />
          </button>
          <SidebarContent onItemClick={() => setOpen(false)} variant={variant} />
        </div>
      </nav>
    </>
  )
}

export default AdminSidebar
